/**
 * 四子棋游戏组件
 * 8x8棋盘，垂直落子，四子连线获胜
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ConnectFourGameState,
  Position,
  ConnectFourMove,
  Player,
  GameMode,
  Difficulty
} from '../types/game';
import {
  createInitialConnectFourState,
  makeConnectFourMove,
  undoConnectFourMove,
  resetConnectFourGame,
  getAvailableColumns,
  getConnectFourStatusText,
  isGameOver,
  getConnectFourAIDecision
} from '../utils/connectfour';
import ConnectFourBoard from './ConnectFourBoard';
import GameShareButton from './GameShareButton';

interface ConnectFourGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
  onGameEnd?: (winner: Player | null, moves: number) => void;
}

const ConnectFourGame: React.FC<ConnectFourGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBackToMenu,
  onBackToModeSelect,
  onGameEnd
}) => {
  const [gameState, setGameState] = useState<ConnectFourGameState>(() =>
    createInitialConnectFourState(gameMode, difficulty)
  );
  const [showEndGameDialog, setShowEndGameDialog] = useState(false);
  const [winningCells, setWinningCells] = useState<Position[]>([]);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const isAIProcessingRef = useRef(false);

  // 处理列点击
  const handleColumnClick = useCallback((col: number) => {
    if (gameState.status !== 'playing' || gameState.isAIThinking || isAIProcessingRef.current) {
      return;
    }

    // 如果是AI模式且当前玩家是AI，则不允许点击
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white') {
      return;
    }

    // 执行移动
    const newGameState = makeConnectFourMove(gameState, col);

    // 检查是否获胜
    if (newGameState.winner) {
      // 找出获胜路径
      const winningPath = findWinningPath(newGameState.board, newGameState.winner);
      setWinningCells(winningPath);
    }

    setGameState(newGameState);
  }, [gameState, gameMode]);

  // 寻找获胜路径
  const findWinningPath = (board: (Player | null)[][], winner: Player): Position[] => {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if (board[row][col] === winner) {
          const path = checkConnectFourPath(board, { row, col }, winner);
          if (path.length >= 4) {
            return path;
          }
        }
      }
    }
    return [];
  };

  // 检查四子连线路径
  const checkConnectFourPath = (
    board: (Player | null)[][],
    start: Position,
    player: Player
  ): Position[] => {
    const directions = [
      { dr: 0, dc: 1 },  // 水平
      { dr: 1, dc: 0 },  // 垂直
      { dr: 1, dc: 1 },  // 右下对角线
      { dr: 1, dc: -1 }  // 左下对角线
    ];

    for (const { dr, dc } of directions) {
      const path = [{ ...start }];
      let r = start.row + dr;
      let c = start.col + dc;

      // 向正方向检查
      while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
        path.push({ row: r, col: c });
        r += dr;
        c += dc;
      }

      // 如果找到4个或以上，向负方向也检查
      if (path.length >= 4) {
        r = start.row - dr;
        c = start.col - dc;
        while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
          path.unshift({ row: r, col: c });
          r -= dr;
          c -= dc;
        }

        if (path.length >= 4) {
          return path.slice(0, 4); // 只返回前4个
        }
      }
    }

    return [];
  };

  // AI移动处理
  const handleAIMove = useCallback(async () => {
    // 检查是否应该执行AI移动
    if (gameMode !== 'pvc' || gameState.currentPlayer !== 'white' ||
        gameState.status !== 'playing' || isAIProcessingRef.current) {
      return;
    }

    // 设置AI处理标志
    isAIProcessingRef.current = true;
    setGameState(prev => ({ ...prev, isAIThinking: true }));

    try {
      // 模拟AI思考时间
      await new Promise(resolve => setTimeout(resolve, 800));

      // 获取AI决策
      const aiDecision = getConnectFourAIDecision(gameState, difficulty);

      if (aiDecision) {
        // 执行AI移动
        const newGameState = makeConnectFourMove(gameState, aiDecision.col);

        // 检查是否获胜
        if (newGameState.winner) {
          const winningPath = findWinningPath(newGameState.board, newGameState.winner);
          setWinningCells(winningPath);
        }

        setGameState(newGameState);
      }
    } catch (error) {
      console.error('AI移动错误:', error);
    } finally {
      isAIProcessingRef.current = false;
      setGameState(prev => ({ ...prev, isAIThinking: false }));
    }
  }, [gameMode, gameState, difficulty]);

  // 悔棋功能
  const handleUndo = useCallback(() => {
    if (gameState.moves.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking) {
      const newGameState = undoConnectFourMove(gameState);
      setGameState(newGameState);
      setWinningCells([]);
    }
  }, [gameState]);

  // 重新开始游戏
  const handleRestart = useCallback(() => {
    isAIProcessingRef.current = false;
    const newGameState = resetConnectFourGame(gameMode, difficulty);
    setGameState(newGameState);
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
    setWinningCells([]);
  }, [gameMode, difficulty]);

  // 监听游戏结束
  useEffect(() => {
    if (gameState.status === 'finished' && !showEndGameDialog && !dialogManuallyClosed) {
      setShowEndGameDialog(true);
      if (onGameEnd) {
        onGameEnd(gameState.winner, gameState.moves.length);
      }
    }
  }, [gameState.status, gameState.winner, showEndGameDialog, dialogManuallyClosed, onGameEnd]);

  // AI移动触发
  useEffect(() => {
    // 只在轮到AI且游戏正在进行时触发AI移动
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' &&
        gameState.status === 'playing' && !gameState.isAIThinking && !isAIProcessingRef.current) {
      handleAIMove();
    }
  }, [gameState.currentPlayer, gameMode, gameState.status, gameState.isAIThinking, handleAIMove]);

  // 游戏结束对话框
  const EndGameDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
        {/* 关闭按钮 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowEndGameDialog(false);
            setDialogManuallyClosed(true);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
          title="关闭"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold text-center mb-4">
          {gameState.winner ? (
            <span className="text-green-600">
              {gameState.winner === 'black' ? '黑方' : '白方'}获胜！
            </span>
          ) : (
            <span className="text-yellow-600">平局！</span>
          )}
        </h2>

        <div className="text-center text-gray-600 mb-6">
          <p>游戏统计：</p>
          <p>总步数: {gameState.moves.length}</p>
          <p>棋盘: 8×8</p>
          {gameMode === 'pvc' && (
            <p>AI难度: {difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'}</p>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={handleRestart}
            className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            再来一局
          </button>
          <button
            onClick={onBackToMenu}
            className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            返回主页
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🔴</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-red-600 via-orange-600 to-yellow-500 bg-clip-text text-transparent sm:mb-1">
                四子棋
              </h1>
              <span className="text-2xl">🔵</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}

            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button
                onClick={onBackToModeSelect}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={gameState.moves.length === 0}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                gameState.moves.length > 0
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title="游戏规则"
            >
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="connectfour" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            <div className={`w-4 h-4 rounded-full ${
              gameState.currentPlayer === 'black' ? 'bg-gray-900' : 'bg-white border-2 border-gray-400'
            }`}></div>
            <span className="text-sm sm:text-base font-medium">
              {gameState.currentPlayer === 'black' ? '黑方回合' : '白方回合'}
            </span>
            {gameState.isAIThinking && (
              <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
            )}
          </div>

          {/* 步数信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>步数: {gameState.moves.length}</span>
          </div>
        </div>
      </div>

      {/* 棋盘 - 优化布局，增大棋盘尺寸 */}
      <div className="flex-1 flex items-center justify-center px-2 py-3 sm:px-4 sm:py-6 min-h-0">
        <div className="w-full h-full flex items-center justify-center">
          <ConnectFourBoard
            board={gameState.board}
            onColumnClick={handleColumnClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking}
            availableColumns={getAvailableColumns(gameState.board)}
            lastMove={gameState.lastMove}
            winningCells={winningCells}
          />
        </div>
      </div>

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center pr-8">
              <span className="text-2xl mr-2">📖</span>
              四子棋游戏规则
            </h2>

            <div className="text-gray-700 space-y-3 text-sm">
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎯 游戏目标</h3>
                <p>率先在棋盘上形成四子连线（横、竖、斜均可）的玩家获胜。</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🎮 游戏规则</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>游戏在8×8的垂直棋盘上进行</li>
                  <li>红方先行，双方轮流选择列投放棋子</li>
                  <li>棋子会自动落到该列最底部的空位</li>
                  <li>先形成四子连线的一方获胜</li>
                  <li>棋盘填满无人获胜则为平局</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">💡 策略提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>中心位置价值最高，连线方式最多</li>
                  <li>注意防守对手的三连威胁</li>
                  <li>构建双重威胁，让对手防不胜防</li>
                  <li>控制关键列，阻断对手的连线</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-1">🔧 操作提示</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>点击列顶部投放棋子</li>
                  <li>使用"悔棋"按钮撤销上一步</li>
                  <li>使用"重开"按钮重新开始游戏</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowRules(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-orange-50 border-t border-orange-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-orange-800 text-center leading-relaxed">
          <strong>规则：</strong>
          8×8垂直棋盘，四子连线获胜 | 棋子从底部向上填充 | 支持水平、垂直、对角线四连 |
          轮流落子，策略对抗
        </p>
      </div>

      {/* 游戏结束对话框 */}
      {showEndGameDialog && <EndGameDialog />}
    </div>
  );
};

export default ConnectFourGame;